import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import dayjs from 'dayjs';
import { BusinessService } from './business.service';
import { UserService } from '../user/user.service';

export const HUBSPOT_CONTACT_LIMIT_TOAST_ID = 'hubspot-contact-limit';
export const HUBSPOT_CONTACT_LIMIT_TOAST_SNOOZE_DAYS = 7;

const SUMMARY_KEY = 'APP.HUBSPOT.CONTACT_LIMIT_TOAST.SUMMARY';
const DETAIL_KEY = 'APP.HUBSPOT.CONTACT_LIMIT_TOAST.DETAIL';

/**
 * Heads-up toast for a connected HubSpot Free portal that hit its contact cap
 * (the API flags the install on the first 402 and clears it once a contact
 * create succeeds again). The toast is sticky; closing it snoozes it for a
 * week per business, so an account still at the cap gets reminded weekly.
 */
@Injectable({
  providedIn: 'root'
})
export class HubspotContactLimitToastService {
  private shownForBusinessId?: string;

  constructor(
    private messageService: MessageService,
    private translate: TranslateService,
    private business: BusinessService,
    private user: UserService,
  ) {
  }

  get shouldShow(): boolean {
    if (!this.business.hasHubspotContactLimitReached) {
      return false;
    }

    const dismissedAt = this.business.hubspotContactLimitToastDismissedAt;

    if (!dismissedAt) {
      return true;
    }

    return dayjs().diff(dayjs(dismissedAt), 'day') >= HUBSPOT_CONTACT_LIMIT_TOAST_SNOOZE_DAYS;
  }

  showIfNeeded(): void {
    const businessId = this.user.activeBusiness?.id;

    if (!businessId || this.shownForBusinessId === businessId) {
      return;
    }

    if (!this.shouldShow) {
      return;
    }

    this.shownForBusinessId = businessId;
    this.translate.get([SUMMARY_KEY, DETAIL_KEY]).subscribe((copy: Record<string, string>) => {
      this.messageService.add({
        severity: 'info',
        sticky: true,
        styleClass: 'p-toast-message-wide',
        summary: copy[SUMMARY_KEY],
        detail: copy[DETAIL_KEY],
        data: { id: HUBSPOT_CONTACT_LIMIT_TOAST_ID },
      });
    });
  }

  handleClose(message?: ToastMessageOptions): void {
    if (message?.data?.id !== HUBSPOT_CONTACT_LIMIT_TOAST_ID) {
      return;
    }

    this.business.dismissHubspotContactLimitToast().subscribe();
  }
}
